/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Switch } from 'react-router-dom';
import Route from 'containers/Route';
import { compose, withProps, branch, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import { removeUrlParam } from './utils/helpers';

import {
	ConfirmationPage,
	UnsubscribePage,
	ErrorPage,
	FacilityPage,
	HomePage,
	// SearchPage,
	SearchPageWrapper,
	UnitPageV2,
	LegalPage,
	LegalReviewPage,
	MarketingLandingPageWrapper,
	MoveInConfirmationPage,
	StatePage, // use during the new state pages redesign
	StorageBasicsPage,
	StyleGuide,
	StyleGuidePage,
	VehicleStoragePage,
	StorageNearMePage,
	ContactPage,
	ClimateControlledPage,
	PSPPage,
	ContactPageV2,
	EmailAuthPage,
	OMIPage,
	PrivacyPolicyPage,
} from 'pages/Bundles';

import { getBooking, updateMoveInConfirmation } from 'store/modules/bookings';
import {
	getHomepage,
	getStateLandingPage,
	getPaidLandingPage,
} from 'store/modules/pages';
import { getUnit } from 'store/modules/units';
import { getFacility } from 'store/modules/facilities';
import {
	getSearchPage,
	getCityLandingPage,
	getVehicleCityLandingPage,
	getAmenityCityLandingPage,
} from 'store/modules/search';
import FacilityPageWrapper from './pages/FacilityPage/FacilityPageWrapper';

export const staticRoutes = [
	{
		path: '/',
		actions: { getHomepage },
		component: HomePage,
		loadData: (props) => props.actions.getHomepage(),
	},
	{
		path: '/contact-us',
		component: ContactPageV2,
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)(/)?',
		component: StatePage,
		actions: { getStateLandingPage },
		loadData: (props) =>
			props.actions.getStateLandingPage(props.match.params.state),
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)/:city([0-9a-zA-Z-]+)(/)?',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getCityLandingPage },
		loadData: (props) =>
			props.actions.getCityLandingPage(
				props.match.params,
				props.queryParams,
				props.locationKey,
			),
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)/:city([0-9a-zA-Z-]+)/:vehicle(car|boat|rv)-storage(/)?',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getVehicleCityLandingPage },
		loadData: (props) =>
			props.actions.getVehicleCityLandingPage(
				props.match.params,
				props.queryParams,
				props.locationKey,
			),
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)/:city([0-9a-zA-Z-]+)/:amenity(24-hour-access|climate-controlled|drive-up-self-storage|electricity|free-truck-rental|mini-self-storage|small-self-storage-units|medium-self-storage-units|large-self-storage-units|self-storage-lockers)(/)?',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getAmenityCityLandingPage },
		loadData: (props) =>
			props.actions.getAmenityCityLandingPage(
				props.match.params,
				props.queryParams,
				props.locationKey,
			),
	},
	{
		path: '/search(/)?',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getSearchPage },
		loadData: (props) =>
			props.actions.getSearchPage(props.queryParams, props.locationKey),
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)/:city([0-9a-zA-Z-]+)(/ogf)?/([0-9a-zA-Z-]+)?-:facilityId([0-9]+)(/)?',
		component: FacilityPageWrapper,
		actions: { getFacility },
		loadData: ({ actions, queryParams, match: { params } }) =>
			actions.getFacility(
				params.facilityId,
				params.city,
				params.state,
				queryParams,
			),
	},
	{
		path: '/facility/:facilityId([0-9]+)(/)?',
		component: FacilityPage,
		actions: { getFacility },
		loadData: ({ actions, match: { params } }) =>
			actions.getFacility(params.facilityId),
	},
	{
		path: '/self-storage/:state([0-9a-zA-Z-]+)/:city([0-9a-zA-Z-]+)/([0-9a-zA-Z-]+)?-:facilityId([0-9]+)/reserve/:unitId([0-9]+)(/)?',
		component: UnitPageV2,
		actions: { getUnit },
		loadData: ({
			actions,
			match: {
				params: { facilityId, unitId },
			},
		}) => actions.getUnit(facilityId, unitId),
	},
	{
		path: '/reservation/confirm-:moveInStatus(.*move-in)/:confirmationCode([A-Za-z0-9]+)(/)?',
		component: MoveInConfirmationPage,
		actions: { updateMoveInConfirmation },
		loadData: ({ actions, match: { params } }) =>
			actions.updateMoveInConfirmation(
				params.confirmationCode,
				params.moveInStatus,
			),
	},
	{
		path: '/reservation/confirmation/:confirmationCode([0-9a-zA-Z_-]+)(/)?',
		actions: { getBooking },
		component: compose(
			connect((state) => {
				return {
					statusCode: state.bookings.statusCode,
				};
			}),
			branch(
				(props) => [401, 403].includes(props.statusCode),
				compose(
					withProps((props) => {
						const { email, ...restQueryParams } =
							props.queryParams || {};
						// Update URL to remove email param
						if (email) {
							removeUrlParam('email');
						}
						return {
							queryParams: restQueryParams,
							confirmationCode:
								props.match.params.confirmationCode,
						};
					}),
					renderComponent(EmailAuthPage),
				),
			),
		)(ConfirmationPage),
		loadData: (props) =>
			props.actions.getBooking(
				props.match.params.confirmationCode,
				props.queryParams,
			),
	},
	{
		path: '/vehicle-storage(/)?',
		component: VehicleStoragePage,
	},
	{
		path: '/storage-units-near-me(/)?',
		component: StorageNearMePage,
	},
	{
		path: '/storage-units-with-climate-control(/)?',
		component: ClimateControlledPage,
	},
	{
		path: '/legal/consumer(/)?',
		component: LegalPage,
	},
	{
		path: '/legal/reviews(/)?',
		component: LegalReviewPage,
	},
	{
		path: '/legal/privacy(/)?',
		component: PrivacyPolicyPage,
	},
	{
		path: '/lpg/lf/:landingPage(hp-copy|storage-location)(/)?',
		component: MarketingLandingPageWrapper,
		actions: { getPaidLandingPage }, // This component/action are only needed for a/b testing
		loadData: (props) =>
			props.actions.getPaidLandingPage(
				props.queryParams,
				props.match.url,
			),
	},
	{
		path: '/unsubscribe(/)?',
		component: UnsubscribePage,
	},
	{
		path: '/contact-selfstorage(/)?',
		component: ContactPage,
	},
	{
		path: '/(what-to-expect|whattoexpect)(/)?',
		component: StorageBasicsPage,
	},
	{
		path: '/ux',
		component: StyleGuide,
	},
	{
		path: '/style-guide',
		component: StyleGuidePage,
	},
	{
		path: '/reservation/online-move-in/:confirmationCode([A-Za-z0-9]+)',
		component: OMIPage,
		actions: { getBooking },
		loadData: (props) =>
			props.actions.getBooking(
				props.match.params.confirmationCode,
				props.queryParams,
			),
	},
	{
		path: '/storage-types/:keyword(rv|car|climate-controlled|portable|motorcycle|commercial|wine|atv|bike|dirt-bike|golf-cart|student|jet-ski|kayak|military|drive-up|24-hour|vehicle|what-is)-self-storage',
		exact: true,
		component: PSPPage,
	},
	{
		path: '/storage-types/self-storage-:keyword(with-electricity|for-boats|truck)',
		exact: true,
		component: PSPPage,
	},
	{
		component: ErrorPage,
	},
];

export default function Routes({ ready }) {
	return (
		<Switch>
			{staticRoutes.map((route, i) => (
				<Route
					key={route.key || i}
					exact
					strict
					ready={ready}
					{...route}
				/>
			))}
		</Switch>
	);
}

Routes.propTypes = {
	ready: PropTypes.bool,
};
